import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';

// Interface for faculty member (API returns just names)
export interface FacultyMember {
    name: string;
}

// Interface for top rated course (matches API response)
export interface TopRatedCourse {
    courseName: string;
    courseId: string;
    type: string;
    feedback: number;
    endDate: string;
    faculties: string[];
}

// Interface for API response
export interface DashboardApiResponse {
    data: {
        topRatedCourses: TopRatedCourse[];
        studentOverview: {
            totalStudents: number;
            applicants: number;
            registrants: number;
        };
    };
}

// Interface for course insights API response (separate API)
export interface CourseInsightsApiResponse {
    data: {
        courses: {
            approved: number;
            rejected: number;
            draft: number;
            pending: number;
            published: number;
            completed: number;
        };
    };
    totalRecords: number;
    errors: any;
    hasNext: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class DashboardService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = environment.API_URL;

    /**
     * Get dashboard data (top rated courses and student overview)
     * @param isMyCourses - true for "My Courses" tab, false for "All Courses" tab
     */
    getDashboardData(isMyCourses: boolean): Observable<DashboardApiResponse> {
        const params = new HttpParams()
            .set('searchParam', '')
            .set('self', isMyCourses.toString())
            .set('isParent', 'false');

        return this.http.get<DashboardApiResponse>(`${this.baseUrl}/course/dashboard`, { params });
    }

    /**
     * Get course insights data (ongoing, completed, approved counts)
     * @param isMyCourses - true for "My Courses" tab, false for "All Courses" tab
     */
    getCourseInsights(isMyCourses: boolean): Observable<CourseInsightsApiResponse> {
        const params = new HttpParams().set('isParent', 'false').set('self', isMyCourses.toString());

        return this.http.get<CourseInsightsApiResponse>(`${this.baseUrl}/course/status-counts`, { params });
    }
}
